// =====================================================
// BACKEND CONFIGURATION
// =====================================================
// Points to YOUR Node/Express backend.
// Salesforce token is NEVER exposed to React.
// =====================================================

import { BASE_URL } from "../BaseUrl";


// =====================================================
// COMMON REQUEST HELPER
// =====================================================

const request = async (endpoint, options = {}) => {
  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });

  const text = await response.text();

  let data = {};

  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {
      error: text || "Invalid server response",
    };
  }

  if (!response.ok) {
    throw new Error(
      data?.error ||
        data?.message ||
        `Request failed with status ${response.status}`
    );
  }

  return data;
};


// =====================================================
// GET ACTIVE SURVEYS
// =====================================================
// Backend:
// GET /api/survey/surveys
//
// Salesforce (Apex REST):
// GET /services/apexrest/npr/surveys
//
// Returns a raw array: [{ id, name, description, collectsContactInfo }, ...]
// =====================================================

export const getActiveSurveys = async () => {
  const response = await request("/survey/surveys", {
    method: "GET",
  });

  return Array.isArray(response) ? response : [];
};


// =====================================================
// GET SURVEY STRUCTURE (stages + questions, EN/ES)
// =====================================================
// Backend:
// GET /api/survey/surveys/:surveyId/structure
//
// Salesforce (Apex REST):
// GET /services/apexrest/npr/surveys/{surveyId}/structure
//
// Returns:
// {
//   surveyId,
//   stages: [
//     {
//       id, name, order,
//       questions: [
//         {
//           id, order, questionType, isRequired,
//           english: { questionText, choices: [] },
//           spanish: { questionText, choices: [] },
//         }
//       ]
//     }
//   ]
// }
// =====================================================

export const getSurveyStructure = async (surveyId) => {
  if (!surveyId) {
    throw new Error("Survey ID is required.");
  }

  const response = await request(
    `/survey/surveys/${encodeURIComponent(surveyId)}/structure`,
    {
      method: "GET",
    }
  );

  return response;
};


// =====================================================
// SUBMIT SURVEY RESPONSE (single call, whole submission)
// =====================================================
// Backend:
// POST /api/survey/surveys/:surveyId/responses
//
// Salesforce (Apex REST):
// POST /services/apexrest/npr/surveys/{surveyId}/responses
//
// Creates the lead, saves all answers, and assigns a nurse
// server-side in one shot. firstName and signatureImage (the HIPAA
// e-signature, as a base64 PNG data URL) are required; answers can be
// an empty array.
//
// Note: communicationConsent is intentionally NOT sent here — it's a
// frontend-only gate, per current requirements. Only the HIPAA
// signature is persisted to Salesforce.
//
// Returns: { success, leadId, assignedNurseId }
// =====================================================

// export const submitSurveyResponse = async (surveyId, {
//   firstName,
//   lastName,
//   email,
//   phone,
//   city,
//   country,
//   zip,
//   dateOfBirth,
//   signatureImage,
//   answers,
// }) => {
//   if (!surveyId) {
//     throw new Error("Survey ID is required.");
//   }

//   if (!firstName) {
//     throw new Error("First name is required.");
//   }

//   if (!signatureImage) {
//     throw new Error("HIPAA signature is required.");
//   }

//   const response = await request(
//     `/survey/surveys/${encodeURIComponent(surveyId)}/responses`,
//     {
//       method: "POST",
//       body: JSON.stringify({
//         firstName,
//         lastName,
//         email,
//         phone,
//         city,
//         country,
//         zip,
//         dateOfBirth,
//         signatureImage,
//         answers: Array.isArray(answers) ? answers : [],
//       }),
//     }
//   );

//   return response;
// };

// Tracking values (utm_*, fbclid, campaign_id/adset_id/ad_id, landing_page_url,
// website_submission_id) are sent at the ROOT of the body, as in doc section 5.4.
export const submitSurveyResponse = async (surveyId, {
  firstName,
  lastName,
  email,
  phone,
  city,
  country,
  zip,
  dateOfBirth,
  signatureImage,
  answers,
  tracking = {},
}) => {
  if (!surveyId) {
    throw new Error("Survey ID is required.");
  }

  if (!firstName) {
    throw new Error("First name is required.");
  }

  if (!signatureImage) {
    throw new Error("HIPAA signature is required.");
  }

  const response = await request(
    `/survey/surveys/${encodeURIComponent(surveyId)}/responses`,
    {
      method: "POST",
      body: JSON.stringify({
        ...tracking,
        firstName,
        lastName,
        email,
        phone,
        city,
        country,
        zip,
        dateOfBirth,
        signatureImage,
        answers: Array.isArray(answers) ? answers : [],
      }),
    }
  );

  return response;
};